interface ManagerAlertDto {
  ManagerAlert_guid: string;
  ManagerAlert_Title: string;
  ManagerAlert_Desc: string;
  ManagerAlert_Url: string | null;
  ManagerAlert_Active: boolean;
  ManagerAlert_CreatedAt: string;
  ManagerAlert_UpdatedAt: string;
}

interface CreateManagerAlertDto {
  ManagerAlert_Title: string;
  ManagerAlert_Desc: string;
  ManagerAlert_Url?: string | null;
  ManagerAlert_Active?: boolean;
}

interface GetManagerAlertsOptions {
  from?: number;
  to?: number;
  ManagerAlert_Active?: boolean;
  ManagerAlert_Title?: string;
  ManagerAlert_Desc?: string;
  ManagerAlert_CreatedAt_From?: string;
  ManagerAlert_CreatedAt_To?: string;
}

class ManagerAlertApi {
  // Get manager alerts
  static async getManagerAlerts(options: GetManagerAlertsOptions = {}): Promise<ManagerAlertDto[]> {
    const params = new URLSearchParams();
    
    if (options.from !== undefined) params.append('from', options.from.toString());
    if (options.to !== undefined) params.append('to', options.to.toString());
    if (options.ManagerAlert_Active !== undefined) params.append('ManagerAlert_Active', options.ManagerAlert_Active.toString());
    if (options.ManagerAlert_Title) params.append('ManagerAlert_Title', options.ManagerAlert_Title);
    if (options.ManagerAlert_Desc) params.append('ManagerAlert_Desc', options.ManagerAlert_Desc);
    if (options.ManagerAlert_CreatedAt_From) params.append('ManagerAlert_CreatedAt_From', options.ManagerAlert_CreatedAt_From);
    if (options.ManagerAlert_CreatedAt_To) params.append('ManagerAlert_CreatedAt_To', options.ManagerAlert_CreatedAt_To);

    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-alerts?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const alerts: ManagerAlertDto[] = await response.json();
      return alerts;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch manager alerts' }));
      throw new Error(errorData.message || 'Failed to fetch manager alerts');
    }
  }

  // Get specific manager alert
  static async getManagerAlert(alertGuid: string): Promise<ManagerAlertDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-alerts/${alertGuid}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const alert: ManagerAlertDto = await response.json();
      return alert;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch manager alert' }));
      throw new Error(errorData.message || 'Failed to fetch manager alert');
    }
  }

  // Create manager alert (managers only)
  static async createManagerAlert(alertData: CreateManagerAlertDto): Promise<ManagerAlertDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-alerts/manager`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(alertData),
    });

    if (response.ok) {
      const alert: ManagerAlertDto = await response.json();
      return alert;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create manager alert' }));
      throw new Error(errorData.message || 'Failed to create manager alert');
    }
  }

  // Update manager alert (managers only)
  static async updateManagerAlert(alertGuid: string, alertData: Partial<CreateManagerAlertDto>): Promise<ManagerAlertDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-alerts/manager/${alertGuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(alertData),
    });

    if (response.ok) {
      const alert: ManagerAlertDto = await response.json();
      return alert;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update manager alert' }));
      throw new Error(errorData.message || 'Failed to update manager alert');
    }
  }

  // Dismiss alert (set active to false)
  static async dismissAlert(alertGuid: string): Promise<ManagerAlertDto> {
    return this.updateManagerAlert(alertGuid, { ManagerAlert_Active: false });
  }

  // Delete manager alert (managers only)
  static async deleteManagerAlert(alertGuid: string): Promise<void> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-alerts/manager/${alertGuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete manager alert' }));
      throw new Error(errorData.message || 'Failed to delete manager alert');
    }
  }

  // Dismiss all active alerts
  static async dismissAllAlerts(): Promise<void> {
    // Get all active alerts first
    const activeAlerts = await this.getManagerAlerts({ ManagerAlert_Active: true });
    
    // Dismiss each alert
    const dismissPromises = activeAlerts.map(alert => 
      this.dismissAlert(alert.ManagerAlert_guid)
    );
    
    await Promise.all(dismissPromises);
  }
}

export default ManagerAlertApi;
export type { ManagerAlertDto, CreateManagerAlertDto, GetManagerAlertsOptions };